/**
 * ARGOV Connect — Meetings Dashboard
 * ARGOV-EVENTS-CONNECT-01 | Phase 1
 *
 * Shows: stats cards, upcoming meetings, all-meetings table with filters.
 * Capability required: meeting.read
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Video, CalendarClock, CheckCircle2, XCircle, Plus, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  DRAFT:       "bg-slate-100 text-slate-700",
  SCHEDULED:   "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-yellow-100 text-yellow-700",
  COMPLETED:   "bg-green-100 text-green-700",
  CANCELLED:   "bg-red-100 text-red-700",
  NO_SHOW:     "bg-orange-100 text-orange-700",
};

const PROVIDER_LABELS = {
  GOOGLE_MEET: "Google Meet",
  ZOOM:        "Zoom",
  MS_TEAMS:    "Teams",
  WEBEX:       "Webex",
  OTHER:       "Other",
};

function formatDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="bg-white border border-slate-200 hover:shadow-md transition-shadow">
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-slate-800">{value ?? "—"}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ConnectMeetings() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [stats,    setStats]    = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);

  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterType,   setFilterType]   = useState("ALL");
  const [search,       setSearch]       = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, upRes, allRes] = await Promise.all([
        fetch(`${API}/api/connect/meetings/stats`,    { credentials: "include" }),
        fetch(`${API}/api/connect/meetings/upcoming`, { credentials: "include" }),
        fetch(`${API}/api/connect/meetings?limit=200`, { credentials: "include" }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (upRes.ok)    setUpcoming((await upRes.json()).results || []);
      if (allRes.ok) {
        const d = await allRes.json();
        setMeetings(d.results || []);
        setTotal(d.total || 0);
      }
    } catch (e) {
      toast.error("Failed to load meetings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = meetings.filter(m => {
    const matchStatus = filterStatus === "ALL" || m.status === filterStatus;
    const matchType   = filterType   === "ALL" || m.meeting_type === filterType;
    const matchSearch = !search ||
      m.title?.toLowerCase().includes(search.toLowerCase()) ||
      m.meeting_ref?.toLowerCase().includes(search.toLowerCase()) ||
      m.host_name?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchType && matchSearch;
  });

  return (
    <div className="space-y-6 p-1" data-testid="connect-meetings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="connect-meetings-title">
            ARGOV Connect
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Video meeting management · Phase 1</p>
        </div>
        <Button
          data-testid="connect-schedule-btn"
          onClick={() => navigate("/connect/schedule")}
          className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          <Plus className="w-4 h-4" /> Schedule Meeting
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="connect-stats-grid">
        <StatCard icon={Video}         label="Total"       value={stats?.total}       color="bg-indigo-50 text-indigo-600" />
        <StatCard icon={CalendarClock} label="Upcoming 7d" value={stats?.upcoming_7d} color="bg-blue-50 text-blue-600" />
        <StatCard icon={CheckCircle2}  label="Completed"   value={stats?.completed}   color="bg-green-50 text-green-600" />
        <StatCard icon={XCircle}       label="Cancelled"   value={stats?.cancelled}   color="bg-red-50 text-red-600" />
      </div>

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <div data-testid="connect-upcoming-section">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
            Upcoming (next 7 days)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {upcoming.map(m => (
              <Card
                key={m.id}
                data-testid={`connect-upcoming-card-${m.id}`}
                className="bg-white border border-slate-200 hover:shadow-md cursor-pointer transition-shadow"
                onClick={() => navigate(`/connect/meetings/${m.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <p className="font-semibold text-slate-800 text-sm leading-snug line-clamp-2">
                      {m.title}
                    </p>
                    <Badge className={`text-xs ml-2 flex-shrink-0 ${STATUS_COLORS[m.status]}`}>
                      {m.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mb-1">{m.meeting_ref}</p>
                  <p className="text-xs text-slate-600 mb-3">{formatDT(m.scheduled_at)}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">{PROVIDER_LABELS[m.provider] || m.provider}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`connect-join-btn-${m.id}`}
                      className="text-xs h-7 gap-1"
                      onClick={e => { e.stopPropagation(); window.open(m.meeting_url, "_blank", "noopener"); }}
                    >
                      <ExternalLink className="w-3 h-3" /> Join
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* All Meetings Table */}
      <div data-testid="connect-all-meetings-section">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            All Meetings ({total})
          </h2>
          <div className="flex flex-1 items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                placeholder="Search title, ref, host…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="connect-search-input"
                className="pl-8 h-9 text-sm"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="connect-filter-status" className="w-36 h-9 text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Statuses</SelectItem>
                {["DRAFT","SCHEDULED","IN_PROGRESS","COMPLETED","CANCELLED","NO_SHOW"].map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger data-testid="connect-filter-type" className="w-40 h-9 text-sm">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Types</SelectItem>
                {["CONSULTATION","FOLLOW_UP","INTERNAL","VENUE_WALKTHROUGH","SUPPLIER_NEGOTIATION","ONBOARDING","REVIEW","OTHER"].map(t => (
                  <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400" data-testid="connect-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400" data-testid="connect-empty">
            <Video className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No meetings found.</p>
            <Button
              variant="outline"
              className="mt-4 text-sm"
              onClick={() => navigate("/connect/schedule")}
            >
              Schedule your first meeting
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="connect-meetings-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["Ref", "Title", "Type", "Provider", "Scheduled", "Duration", "Status", "Host"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((m, i) => (
                  <tr
                    key={m.id}
                    data-testid={`connect-meeting-row-${m.id}`}
                    className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${i % 2 === 0 ? "" : "bg-slate-50/30"}`}
                    onClick={() => navigate(`/connect/meetings/${m.id}`)}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{m.meeting_ref}</td>
                    <td className="px-4 py-3 font-medium text-slate-800 max-w-[200px] truncate">{m.title}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{m.meeting_type?.replace("_", " ")}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{PROVIDER_LABELS[m.provider] || m.provider}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{formatDT(m.scheduled_at)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{m.duration_minutes}m</td>
                    <td className="px-4 py-3">
                      <Badge className={`text-xs ${STATUS_COLORS[m.status]}`}>{m.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 max-w-[120px] truncate">{m.host_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
